import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  ArrowLeft,
  CalendarBlank,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import BuyerSelectedMaterialsTable from "@/components/BuyerSelectedMaterialsTable";
import CityGoogleAutocomplete from "@/components/CityGoogleAutocomplete";
import { DURATION_OPTIONS } from "@/lib/buyerRfqConstants";
import {
  clearBuyerRfqDraft,
  emptyBuyerRfqDraft,
  loadBuyerRfqDraft,
  mergeBuyerRfqDraft,
} from "@/lib/buyerRfqDraft";
import { useAuth } from "@/lib/auth";
import { mergeDraftWithBuyerProfile, syncBuyerProfileToDraft } from "@/lib/buyerProfile";
import { parseProductSizes, lineSizeLabel, catalogProductIdFromLineKey } from "@/lib/productSizes";

export default function BuyerRequirementPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [bootstrapped, setBootstrapped] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [city, setCity] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [requirementType, setRequirementType] = useState("rent");
  const [duration, setDuration] = useState("");
  const [customDuration, setCustomDuration] = useState("");
  const [phone, setPhone] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [company, setCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    const d = mergeDraftWithBuyerProfile(loadBuyerRfqDraft() || emptyBuyerRfqDraft(), user);
    setSelectedProducts(d.selectedProducts || {});
    setCity(d.city || "");
    setStartDate(d.startDate);
    setRequirementType(d.requirementType || "rent");
    setDuration(d.duration || "");
    setCustomDuration(d.customDuration || "");
    setPhone(d.phone || "");
    setBuyerName(d.buyerName || "");
    setCompany(d.company || "");
    if (user?.role === "buyer") syncBuyerProfileToDraft(user);
    setBootstrapped(true);
  }, [authLoading, user]);

  const selectedCount = Object.keys(selectedProducts).length;

  useEffect(() => {
    if (!bootstrapped) return;
    if (selectedCount === 0) {
      mergeBuyerRfqDraft({ selectedProducts: {} });
      navigate("/materials", { replace: true });
    }
  }, [bootstrapped, selectedCount, navigate, selectedProducts]);

  useEffect(() => {
    if (!bootstrapped) return;
    mergeBuyerRfqDraft({
      selectedProducts,
      city,
      startDate,
      requirementType,
      duration,
      customDuration,
      phone,
      buyerName,
      company,
    });
  }, [
    bootstrapped,
    selectedProducts,
    city,
    startDate,
    requirementType,
    duration,
    customDuration,
    phone,
    buyerName,
    company,
  ]);

  const parseAreaValue = (raw) => {
    const n = parseFloat(String(raw ?? "").replace(/,/g, "").trim());
    return Number.isFinite(n) ? n : NaN;
  };

  const handleSubmit = async () => {
    const entries = Object.entries(selectedProducts);
    if (entries.length === 0) {
      toast.error("Select at least one product");
      return;
    }

    for (const [lineKey, p] of entries) {
      const sizeOpts = parseProductSizes(p.sizes);
      const sizeLabel = lineSizeLabel(lineKey, p);
      if (sizeOpts.length > 0 && !sizeLabel && !p.isCustom) {
        toast.error(`Select a size for “${p.name}”`);
        return;
      }
      if (p.specType === "area") {
        const av = parseAreaValue(p.areaValue);
        if (!Number.isFinite(av) || av <= 0) {
          toast.error(`Enter a valid area greater than 0 for “${p.name}”`);
          return;
        }
      } else if (!p.quantity || p.quantity < 1) {
        toast.error(`Enter a valid quantity (at least 1) for “${p.name}”`);
        return;
      }
    }

    const catalogItems = entries.map(([lineKey, p]) => {
      const base = {
        product_name: p.name,
        product_id: p.isCustom ? null : catalogProductIdFromLineKey(lineKey, p),
      };
      const sizeVal = String(p.size || lineSizeLabel(lineKey, p) || "").trim();
      const withSize = (row) => (sizeVal ? { ...row, size: sizeVal } : row);
      if (p.specType === "area") {
        const area_value = parseAreaValue(p.areaValue);
        return withSize({
          ...base,
          spec_type: "area",
          area_value,
          area_unit: p.areaUnit === "sqm" ? "sqm" : "sqft",
          quantity: null,
        });
      }
      return withSize({
        ...base,
        spec_type: "quantity",
        quantity: p.quantity,
        area_value: null,
        area_unit: null,
      });
    });
    if (!city) {
      toast.error("Select a city");
      return;
    }
    if (!startDate) {
      toast.error("Select a start date");
      return;
    }
    const finalDuration =
      requirementType === "rent" ? (duration === "custom" ? customDuration : duration) : "N/A";
    if (requirementType === "rent" && !String(finalDuration || "").trim()) {
      toast.error("Select or enter duration");
      return;
    }
    if (!phone || phone.length < 10) {
      toast.error("Enter a valid phone number");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/leads", {
        buyer_phone: phone,
        buyer_name: buyerName,
        buyer_company: company,
        city,
        start_date: format(startDate, "yyyy-MM-dd"),
        duration: finalDuration,
        requirement_type: requirementType,
        items: catalogItems,
        source: "platform",
      });
      clearBuyerRfqDraft();
      navigate("/thank-you", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    }
    setSubmitting(false);
  };

  const lineCountLabel =
    selectedCount === 1 ? "1 product selected" : `${selectedCount} products selected`;

  if (!bootstrapped || selectedCount === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6]">
        <p className="text-sm text-neutral-500">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6] pb-24">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-neutral-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-start gap-3">
          <Button
            type="button"
            variant="outline"
            size="icon"
            data-testid="requirement-back-btn"
            className="rounded-full border-neutral-200 shrink-0"
            onClick={() => navigate("/materials")}
            aria-label="Back to materials"
          >
            <ArrowLeft size={20} weight="bold" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-neutral-900 tracking-tight">Your Requirement</h1>
            <p className="text-sm text-neutral-500 mt-0.5">{lineCountLabel}</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 pt-6 space-y-5">
        <BuyerSelectedMaterialsTable
          selectedProducts={selectedProducts}
          setSelectedProducts={setSelectedProducts}
          testIdPrefix="req"
        />

        <Card className="overflow-visible rounded-xl border-neutral-200 shadow-sm">
          <CardContent className="p-5 space-y-1">
            <h2 className="text-base font-bold text-neutral-900 mb-3">Delivery &amp; project details</h2>
            <p className="text-xs text-neutral-500 mb-4">
              City is where you need materials. Other fields match your project timeline and how to reach you.
            </p>
            <div className="space-y-4">
              <div className="relative z-20">
                <label
                  htmlFor="req-city-search"
                  className="text-sm font-medium text-neutral-700 mb-1 block"
                >
                  City *
                </label>
                <CityGoogleAutocomplete
                  value={city}
                  onChange={setCity}
                  placeholder="Search city…"
                  className="w-full"
                  predictionsPlacement="auto"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-700 mb-1 block">Requirement type *</label>
                <Select
                  value={requirementType}
                  onValueChange={(v) => {
                    setRequirementType(v);
                    if (v !== "rent") {
                      setDuration("");
                      setCustomDuration("");
                    }
                  }}
                >
                  <SelectTrigger data-testid="req-type-select" className="rounded-lg border-neutral-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rent">Rent</SelectItem>
                    <SelectItem value="buy">Buy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-700 mb-1 block">Start date *</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="req-start-date"
                      variant="outline"
                      className="w-full justify-start rounded-lg border-neutral-300 font-normal"
                    >
                      <CalendarBlank size={16} className="mr-2 text-neutral-500" />
                      {startDate ? format(startDate, "dd MMM yyyy") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={startDate} onSelect={setStartDate} />
                  </PopoverContent>
                </Popover>
              </div>
              {requirementType === "rent" && (
                <div>
                  <label className="text-sm font-medium text-neutral-700 mb-1 block">Duration *</label>
                  <Select
                    value={duration}
                    onValueChange={(v) => {
                      setDuration(v);
                      if (v !== "custom") setCustomDuration("");
                    }}
                  >
                    <SelectTrigger data-testid="req-duration-select" className="rounded-lg border-neutral-300">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      {DURATION_OPTIONS.map((d) => (
                        <SelectItem key={d} value={d}>
                          {d}
                        </SelectItem>
                      ))}
                      <SelectItem value="custom">Custom (type your own)</SelectItem>
                    </SelectContent>
                  </Select>
                  {duration === "custom" && (
                    <Input
                      data-testid="req-custom-duration"
                      placeholder='e.g. "45 Days"'
                      value={customDuration}
                      onChange={(e) => setCustomDuration(e.target.value)}
                      className="mt-2 rounded-lg border-neutral-300"
                    />
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-neutral-200 shadow-sm">
          <CardContent className="p-5 space-y-4">
            <h2 className="text-base font-bold text-neutral-900">Contact</h2>
            {user?.role === "buyer" ? (
              <p className="text-xs text-neutral-500">
                Signed in as {user.name || user.email}.{" "}
                <Link to="/account" className="text-[#FF7A00] font-semibold hover:underline">
                  Edit saved details
                </Link>
              </p>
            ) : (
              <p className="text-xs text-neutral-500">
                <Link
                  to="/account/login?next=/requirement"
                  className="text-[#FF7A00] font-semibold hover:underline"
                >
                  Sign in
                </Link>{" "}
                to save your details and auto-fill this form next time.
              </p>
            )}
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Contact number *</label>
              <Input
                data-testid="req-phone"
                placeholder="Enter 10-digit phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="rounded-lg border-neutral-300"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Your name</label>
              <Input
                data-testid="req-buyer-name"
                placeholder="Optional"
                value={buyerName}
                onChange={(e) => setBuyerName(e.target.value)}
                className="rounded-lg border-neutral-300"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Company name</label>
              <Input
                data-testid="req-company"
                placeholder="Optional"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="rounded-lg border-neutral-300"
              />
            </div>
          </CardContent>
        </Card>

        <Button
          data-testid="submit-rfq-btn"
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-xl h-14 text-lg font-bold shadow-[4px_4px_0px_#0A0A0A] hover:shadow-[2px_2px_0px_#0A0A0A]"
        >
          {submitting ? "Submitting…" : `Submit requirement (${selectedCount} items)`}
        </Button>
      </main>
    </div>
  );
}
